import { ClipboardList, Building2, Plane, Camera, MessageSquareHeart, Shield, Award, Clock, HeartHandshake, TrendingDown, ChevronRight, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
const steps = [{
  icon: ClipboardList,
  step: "01",
  title: "Book Procedure",
  description: "Select your treatment and get a personalized quote within 24 hours"
}, {
  icon: Building2,
  step: "02",
  title: "Accommodation",
  description: "We arrange your stay at partner hotels near the hospital"
}, {
  icon: Plane,
  step: "03",
  title: "Travel",
  description: "Visa assistance, airport pickup, and seamless transfers"
}, {
  icon: Camera,
  step: "04",
  title: "Sightsee",
  description: "Explore India during your recovery with curated experiences"
}, {
  icon: MessageSquareHeart,
  step: "05",
  title: "Follow-Up",
  description: "Regular check-ins and feedback to ensure your continued recovery"
}];
const trustBadges = [{
  icon: Shield,
  label: "JCI Accredited"
}, {
  icon: TrendingDown,
  label: "Save 50-70%"
}, {
  icon: Award,
  label: "500+ Procedures"
}, {
  icon: Clock,
  label: "24/7 Support"
}, {
  icon: HeartHandshake,
  label: "100% Care Guarantee"
}];
export function StepsSection() {
  return <section className="py-16 lg:py-24 bg-muted/50">
      <div className="container mx-auto px-4">
        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12 lg:mb-16">
          {trustBadges.map(badge => <Badge key={badge.label} variant="secondary" className="px-4 py-2 text-sm font-medium gap-2 bg-card border border-border shadow-sm text-primary">
              <badge.icon className="w-4 h-4 text-primary" />
              {badge.label}
            </Badge>)}
        </div>

        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-4">
            Your Journey
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4">
             5 Steps to Your Perfect Medical Trip
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base lg:text-lg">
            TrustedHeal connects you with licensed, globally recognized providers for seamless medical travel with complete peace of mind.
          </p>
        </div>

        {/* Mobile Timeline View */}
        <div className="lg:hidden">
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20" />
            
            <div className="space-y-0">
              {steps.map((step, index) => <div key={step.title} className="relative flex gap-4">
                  {/* Timeline Node */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shadow-lg border-4 border-background">
                      {step.step}
                    </div>
                    {/* Connector Arrow */}
                    {index < steps.length - 1 && <div className="flex flex-col items-center py-2">
                        <div className="w-0.5 h-8 bg-primary/30" />
                        <ChevronDown className="w-4 h-4 text-primary/50 -mt-1" />
                      </div>}
                  </div>
                  
                  {/* Content Card */}
                  <div className="flex-1 pb-6">
                    <div className="bg-card rounded-xl p-5 shadow-soft border border-border/50 transition-all duration-300 hover:shadow-hover hover:border-primary/30">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <step.icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display text-lg font-semibold text-secondary mb-1">
                            {step.title}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Desktop Horizontal View */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Horizontal Connection Line */}
            <div className="absolute top-[3.5rem] left-[10%] right-[10%] h-1 z-0">
              <div className="w-full h-full bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 rounded-full" />
              {/* Animated dots on the line */}
              <div className="absolute inset-0 flex items-center justify-between px-[12%]">
                {[0, 1, 2, 3].map(i => <div key={i} className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-primary/40" />
                  </div>)}
              </div>
            </div>

            <div className="grid grid-cols-5 gap-6">
              {steps.map((step, index) => <div key={step.title} className="relative group" style={{
              animationDelay: `${index * 0.1}s`
            }}>
                  {/* Step Number - On the timeline */}
                  <div className="flex justify-center mb-6 relative z-10">
                    <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-lg border-4 border-background transition-transform duration-300 group-hover:scale-110">
                      {step.step}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="bg-card rounded-2xl p-6 shadow-soft border border-border/50 h-full transition-all duration-300 hover:shadow-hover hover:border-primary/30 hover:-translate-y-2">
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-colors group-hover:bg-primary/20 mx-auto">
                      <step.icon className="w-7 h-7 text-primary" />
                    </div>

                    {/* Content */}
                    <div className="text-center">
                      <h3 className="font-display text-xl font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}